import React from 'react';

import { useTranslation } from 'react-i18next';

import { Card, Heading, Text, Box, HStack, VStack, Icon } from '@gluestack-ui/themed';

import { FileText, AlignLeft, Eye, NotebookText, Search, Blocks } from 'lucide-react-native';
import { useTenantGradingSettings } from '../../../common/context/TenantSettingsStore';

// Icon mapping for different evaluation dimensions
const getDimensionIcon = (dimension) => {
  const iconMap = {
    description: NotebookText,
    analysis: Search,
    application: Blocks,
    formal: AlignLeft,
    reflection: Eye,
  };
  return iconMap[dimension] || FileText; // fallback to FileText
};

const ObjectiveBox = ({ dimension, objectives, evalDimensions, dimensionColors }) => {
  const { t } = useTranslation();
  // Get dimension info
  const dimensionInfo = evalDimensions[dimension] || {};
  const dimensionLabel = dimensionInfo.label || dimension;
  const IconComponent = getDimensionIcon(dimension);
  const iconColor = dimensionColors[dimension] || '$textLight500';

  if (!objectives?.applicable) return null;

  return (
    <Box width="48%">
      <Text size="2xs" textTransform="uppercase" color="$textLight400" mb="$1">
        {dimensionLabel}
      </Text>

      {!objectives.applicable && <Text size="xs">{t('student.module.task-overview.objectives.not-applicable')}</Text>}
      {objectives.demands.map((objective, idx) => (
        <HStack key={idx} space="sm" mb="$1" alignItems="flex-start">
          <Box
            width={24}
            height={24}
            borderRadius={4}
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            mt="$0.5"
          >
            <Icon as={IconComponent} size="sm" color={iconColor} />
          </Box>
          <Text size="xs" flex={1}>
            {objective}
          </Text>
        </HStack>
      ))}
    </Box>
  );
};

const TaskObjectives = ({ objectives, ...props }) => {
  const { t } = useTranslation();
  const { evalDimensionKeys, evalDimensions, dimensionColors } = useTenantGradingSettings();

  // check if empty, so no objectives or all values of keys are empty
  const isEmpty = !objectives || Object.values(objectives).every((dim) => dim.applicable === false);

  // Create rows of dimensions (2 per row)
  const dimensionRows = React.useMemo(() => {
    const rows = [];
    const availableDimensions = evalDimensionKeys.filter((dim) => objectives?.[dim]?.applicable);

    for (let i = 0; i < availableDimensions.length; i += 2) {
      rows.push(availableDimensions.slice(i, i + 2));
    }
    return rows;
  }, [evalDimensionKeys, objectives]);

  if (isEmpty) return null;
  return (
    <Card testID="task-objectives" {...props}>
      <Heading mb="$3">{t('student.module.task-overview.objectives.title')}</Heading>
      <VStack space="lg">
        {dimensionRows.map((row, rowIndex) => (
          <HStack key={`row-${rowIndex}`} space="lg">
            {row.map((dimension) => (
              <ObjectiveBox
                key={dimension}
                dimension={dimension}
                objectives={objectives?.[dimension]}
                evalDimensions={evalDimensions}
                dimensionColors={dimensionColors}
              />
            ))}
          </HStack>
        ))}
      </VStack>
    </Card>
  );
};

export default TaskObjectives;
